import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, RefreshCw, Package } from "lucide-react";

function OrderRow({ order, onReorder }) {
  const [busy, setBusy] = useState(false);
  const doReorder = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/orders/${order.id}/reorder`);
      toast.success(`${data.added} item(s) added to your bag`);
    } catch { toast.error("Could not re-add these items"); }
    setBusy(false);
  };
  const invoiceUrl = `${API}/orders/${order.id}/invoice`;
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const openInvoice = async () => {
    // fetch with auth then open blob in new tab so httpOnly + Bearer both work
    try {
      const res = await fetch(invoiceUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch { toast.error("Could not open invoice"); }
  };

  return (
    <div className="border border-[#EAE5D9] p-6" data-testid={`order-row-${order.id}`}>
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <p className="label-uppercase text-[#4A4A4A]">Order · {order.id.slice(0,8)}</p>
          <p className="font-serif text-2xl mt-1">${order.total.toFixed(2)}</p>
          <p className="text-xs text-[#4A4A4A] mt-1">
            {new Date(order.created_at).toLocaleDateString(undefined, { year:"numeric", month:"long", day:"numeric" })}
            {" · "}{order.items.length} item(s)
          </p>
        </div>
        <div className="text-right">
          <span className="label-uppercase text-[#2C402E]">{order.status}</span>
          {order.shabbat_hold && (
            <p className="label-uppercase text-[#B24C38] mt-1">Shabbat hold</p>
          )}
        </div>
      </div>
      <ul className="text-sm mt-4 space-y-1">
        {order.items.map((it, i) => (
          <li key={i} className="text-[#4A4A4A]">
            · {it.quantity} × {it.name}
            {it.size && ` · Size ${it.size}`}
            {it.color && ` · ${it.color}`}
          </li>
        ))}
      </ul>
      <div className="flex gap-3 mt-6">
        <button onClick={openInvoice} className="btn-outline-ink text-xs" data-testid={`order-invoice-${order.id}`}>
          <FileText className="w-3 h-3 inline mr-2"/> Download invoice
        </button>
        <button onClick={doReorder} disabled={busy} className="btn-terracotta text-xs" data-testid={`order-reorder-${order.id}`}>
          <RefreshCw className="w-3 h-3 inline mr-2"/> {busy ? "Adding…" : "Re-order"}
        </button>
      </div>
    </div>
  );
}

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);

  const load = () => {
    if (!user) { setOrders([]); return; }
    client.get("/orders").then(r => setOrders(r.data)).catch(() => setOrders([]));
  };
  useEffect(load, [user]);

  if (!user) return (
    <div className="container-editorial mt-14 py-20 text-center">
      <p className="label-uppercase text-[#B24C38]">Order history</p>
      <h1 className="font-serif text-5xl tracking-tighter mt-3">Sign in to see your orders.</h1>
      <Link to="/login?redirect=/orders" className="btn-terracotta mt-8 inline-flex">Sign In →</Link>
    </div>
  );

  return (
    <div className="container-editorial mt-8 md:mt-14">
      <div className="border-b border-[#EAE5D9] pb-10 mb-12">
        <p className="label-uppercase text-[#B24C38]">Your orders</p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter mt-3">Order History</h1>
        <p className="text-[#4A4A4A] mt-4 max-w-xl">
          Every piece you've bought on Kesher, ready to invoice or re-order in one click.
        </p>
      </div>

      {orders === null ? (
        <p className="label-uppercase">Loading…</p>
      ) : orders.length === 0 ? (
        <div className="py-24 text-center">
          <Package className="w-10 h-10 mx-auto text-[#4A4A4A]" strokeWidth={1.5}/>
          <p className="font-serif text-3xl mt-6 mb-6">No orders yet.</p>
          <Link to="/shop" className="btn-terracotta">Start shopping →</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="orders-grid">
          {orders.map(o => <OrderRow key={o.id} order={o} onReorder={load}/>)}
        </div>
      )}
    </div>
  );
}
